export type AchievementPool = 'normal' | 'shadow' | 'dungeon' | '';

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;
  icon: string;
  pool: AchievementPool;
  order: number;
}

function bakeAchiev(id: string, name: string, desc: string, icon: string, order: number): AchievementDef {
  return { id, name, desc, icon, pool: 'normal', order };
}

function shadowAchiev(id: string, name: string, desc: string, icon: string, order: number): AchievementDef {
  return { id, name, desc, icon, pool: 'shadow', order };
}

export const ACHIEVEMENTS: AchievementDef[] = [
  // === Cookie baking achievements ===
  bakeAchiev('wake_and_bake', 'Wake and bake', 'Bake 1 cookie in one ascension.', '🍪', 100),
  bakeAchiev('making_some_dough', 'Making some dough', 'Bake 1,000 cookies in one ascension.', '🍪', 101),
  bakeAchiev('so_baked_right_now', 'So baked right now', 'Bake 100,000 cookies in one ascension.', '🍪', 102),
  bakeAchiev('fledgling_bakery', 'Fledgling bakery', 'Bake 1 million cookies in one ascension.', '🍪', 103),
  bakeAchiev('affluent_bakery', 'Affluent bakery', 'Bake 100 million cookies in one ascension.', '🍪', 104),
  bakeAchiev('world_famous_bakery', 'World-famous bakery', 'Bake 10 billion cookies in one ascension.', '🍪', 105),
  bakeAchiev('cosmic_bakery', 'Cosmic bakery', 'Bake 1 trillion cookies in one ascension.', '🍪', 106),
  bakeAchiev('galactic_bakery', 'Galactic bakery', 'Bake 100 trillion cookies in one ascension.', '🍪', 107),
  bakeAchiev('universal_bakery', 'Universal bakery', 'Bake 10 quadrillion cookies in one ascension.', '🍪', 108),
  bakeAchiev('timeless_bakery', 'Timeless bakery', 'Bake 1 quintillion cookies in one ascension.', '🍪', 109),
  bakeAchiev('infinite_bakery', 'Infinite bakery', 'Bake 100 quintillion cookies in one ascension.', '🍪', 110),
  bakeAchiev('immortal_bakery', 'Immortal bakery', 'Bake 10 sextillion cookies in one ascension.', '🍪', 111),

  // === CpS achievements ===
  bakeAchiev('mathematician', 'Mathematician', 'Have at least 1 of everything.', '📐', 200),
  bakeAchiev('base_10', 'Base 10', 'Have at least 10 of everything.', '🔢', 201),
  bakeAchiev('centennial', 'Centennial', 'Have at least 100 of everything.', '💯', 202),

  // === Clicking achievements ===
  bakeAchiev('click', 'Click', 'Click the big cookie 1 time.', '👆', 300),
  bakeAchiev('double_click', 'Double-click', 'Click the big cookie 100 times.', '👆', 301),
  bakeAchiev('mouse_wheel', 'Mouse wheel', 'Click the big cookie 1,000 times.', '👆', 302),
  bakeAchiev('of_mice_and_men', 'Of Mice and Men', 'Click the big cookie 10,000 times.', '👆', 303),
  bakeAchiev('the_digital', 'The Digital', 'Click the big cookie 100,000 times.', '👆', 304),
  bakeAchiev('extreme_polydactyly', 'Extreme polydactyly', 'Click the big cookie 1,000,000 times.', '👆', 305),

  // === Building count achievements ===
  bakeAchiev('cursor_1', 'Click', 'Have 1 cursor.', '👆', 400),
  bakeAchiev('cursor_50', 'Lots of clicking', 'Have 50 cursors.', '👆', 401),
  bakeAchiev('cursor_100', 'Click frenzy', 'Have 100 cursors.', '👆', 402),
  bakeAchiev('cursor_150', 'Click fest', 'Have 150 cursors.', '👆', 403),
  bakeAchiev('cursor_200', 'Cursor commando', 'Have 200 cursors.', '👆', 404),

  bakeAchiev('grandma_1', 'Just wrong', 'Have 1 grandma.', '👵', 410),
  bakeAchiev('grandma_50', 'Sly baker', 'Have 50 grandmas.', '👵', 411),
  bakeAchiev('grandma_100', 'Elderly', 'Have 100 grandmas.', '👵', 412),
  bakeAchiev('grandma_150', 'Bingo center', 'Have 150 grandmas.', '👵', 413),
  bakeAchiev('grandma_200', 'Panic at the bingo', 'Have 200 grandmas.', '👵', 414),

  bakeAchiev('farm_1', 'Bought the farm', 'Have 1 farm.', '🌾', 420),
  bakeAchiev('farm_50', 'Reap what you sow', 'Have 50 farms.', '🌾', 421),
  bakeAchiev('farm_100', 'Cookie field', 'Have 100 farms.', '🌾', 422),
  bakeAchiev('farm_150', 'Cookie garden', 'Have 150 farms.', '🌾', 423),
  bakeAchiev('farm_200', 'Farmception', 'Have 200 farms.', '🌾', 424),

  bakeAchiev('mine_1', 'You know the drill', 'Have 1 mine.', '⛏️', 430),
  bakeAchiev('mine_50', 'Excavation site', 'Have 50 mines.', '⛏️', 431),
  bakeAchiev('mine_100', 'Hollow the planet', 'Have 100 mines.', '⛏️', 432),
  bakeAchiev('mine_150', 'Can you dig it', 'Have 150 mines.', '⛏️', 433),

  bakeAchiev('factory_1', 'Production chain', 'Have 1 factory.', '🏭', 440),
  bakeAchiev('factory_50', 'Industrial revolution', 'Have 50 factories.', '🏭', 441),
  bakeAchiev('factory_100', 'Global warming', 'Have 100 factories.', '🏭', 442),

  bakeAchiev('bank_1', 'Pretty penny', 'Have 1 bank.', '🏦', 450),
  bakeAchiev('bank_50', 'Fit the bill', 'Have 50 banks.', '🏦', 451),
  bakeAchiev('bank_100', 'A loan in the dark', 'Have 100 banks.', '🏦', 452),

  bakeAchiev('temple_1', 'Altar', 'Have 1 temple.', '🛕', 460),
  bakeAchiev('temple_50', 'Pantheon', 'Have 50 temples.', '🛕', 461),

  bakeAchiev('wizard_tower_1', "Bewitched", 'Have 1 wizard tower.', '🧙', 470),
  bakeAchiev('wizard_tower_50', 'The sorcerer', 'Have 50 wizard towers.', '🧙', 471),

  bakeAchiev('shipment_1', 'Expedition', 'Have 1 shipment.', '🚀', 480),
  bakeAchiev('shipment_50', 'Galactic highway', 'Have 50 shipments.', '🚀', 481),

  bakeAchiev('alchemy_lab_1', 'A world of pure imagination', 'Have 1 alchemy lab.', '⚗️', 490),
  bakeAchiev('portal_1', 'Portal', 'Have 1 portal.', '🌀', 500),
  bakeAchiev('time_machine_1', 'Time warp', 'Have 1 time machine.', '⏰', 510),
  bakeAchiev('antimatter_condenser_1', 'Antibatter', 'Have 1 antimatter condenser.', '⚛️', 520),
  bakeAchiev('prism_1', 'Dazzling', 'Have 1 prism.', '🔺', 530),

  // === Golden cookie achievements ===
  bakeAchiev('lucky_cookie', 'Lucky cookie', 'Click 7 golden cookies.', '✨', 600),
  bakeAchiev('seven_horseshoes', 'Seven horseshoes', 'Click 27 golden cookies.', '✨', 601),
  bakeAchiev('golden_cookie', 'Golden cookie', 'Click 77 golden cookies.', '✨', 602),

  // === Shadow achievements ===
  shadowAchiev('cheated_cookies', 'Cheated cookies taste awful', 'Hack in some cookies.', '💀', 900),
  shadowAchiev('third_party', 'Third-party', 'Use an add-on.', '🔌', 901),
  shadowAchiev('true_neverclick', 'True Neverclick', 'Make 1 million cookies with no cookie clicks.', '🚫', 902),
  shadowAchiev('hardcore', 'Hardcore', 'Get 1 billion cookies without upgrades.', '💪', 903),
  shadowAchiev('speed_baking_i', 'Speed baking I', 'Get 1 million cookies in 35 minutes.', '⚡', 904),
  shadowAchiev('speed_baking_ii', 'Speed baking II', 'Get 1 million cookies in 25 minutes.', '⚡', 905),
  shadowAchiev('speed_baking_iii', 'Speed baking III', 'Get 1 million cookies in 15 minutes.', '⚡', 906),
];
